import { useHouseholds, usePlatewell, useRecipeQuota } from '@platewell/query';
import type { BillingSummary } from '@platewell/schema';
import { formatMoney, SUBSCRIPTION_PRICE_USD } from '@platewell/schema';
import { Link } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { billingPortal } from '../../lib/api.js';
import { usePlanPrice } from '../../lib/use-plan-price.js';

/**
 * Billing, for owners only.
 *
 * Everything here comes from the server rather than the document, because a co-owner needs
 * to know *who* is paying and when it renews — facts the CRDT deliberately does not carry.
 * A non-owner, or anyone offline, simply sees nothing; the plan banner and the paywall
 * already cover what they can act on.
 */
export function SubscriptionTab() {
  const { active } = useHouseholds();

  // Under tabs the old `return null` for a non-owner would be a blank panel rather than a
  // section quietly missing from a scroll, so they get the plan without the controls.
  if (active?.role !== 'owner') return <NonOwnerSummary />;
  return <BillingSection />;
}

function NonOwnerSummary() {
  const { active } = useHouseholds();
  const quota = useRecipeQuota();
  const subscribed = active?.plan === 'pro';

  return (
    <section className="section-group">
      <h2 className="section-title">Subscription</h2>
      {subscribed ? (
        <p>
          <strong>Subscribed</strong> — all your recipes for everyone in this household.
        </p>
      ) : (
        <p>
          <strong>Free plan.</strong> {quota.used} of {quota.limit ?? 0} recipes used.
        </p>
      )}
      <p className="hint">Only owners can manage billing for this household.</p>
    </section>
  );
}

function BillingSection() {
  const { active, activeHouseholdId } = useHouseholds();
  const { billing, reloadBilling } = usePlatewell();
  const { formatted: planPrice, loading: planPriceLoading } = usePlanPrice();
  const quota = useRecipeQuota();
  const [error, setError] = useState<string | null>(null);

  // The provider owns the fetch and the cache, so switching households and coming back
  // does not re-request, and the paywall reads the same value this screen does.
  useEffect(() => {
    if (activeHouseholdId == null) return;
    void reloadBilling();
  }, [activeHouseholdId, reloadBilling]);

  if (activeHouseholdId == null) return null;

  async function openPortal(): Promise<void> {
    if (activeHouseholdId == null) return;
    setError(null);
    try {
      const { url } = await billingPortal(activeHouseholdId);
      window.open(url, '_blank', 'noopener');
    } catch {
      setError('Could not open the billing portal. Please try again.');
    }
  }

  const renewal =
    billing?.currentPeriodEnd == null
      ? null
      : new Date(billing.currentPeriodEnd).toLocaleDateString();

  return (
    <section className="section-group">
      <h2 className="section-title">Subscription</h2>

      {billing?.complimentary === true ? (
        <p>
          <strong>Included at no charge.</strong> All your recipes, no cost.
        </p>
      ) : billing?.plan === 'pro' ? (
        <>
          <p>
            <strong>Subscribed</strong> — all your recipes for everyone in this household.
          </p>
          <div>
            <div className="kv">
              <span>{billing.cancelAtPeriodEnd ? 'Access until' : 'Renews'}</span>
              <span>{renewal ?? 'Unknown'}</span>
            </div>
            {billing.subscribedBy != null && (
              <div className="kv">
                <span>Subscribed by</span>
                <span>{billing.subscribedBy}</span>
              </div>
            )}
            {billing.store != null && (
              <div className="kv">
                <span>Bought through</span>
                <span>{storeLabel(billing.store)}</span>
              </div>
            )}
            {billing.currency != null && billing.amountMinor != null && (
              <div className="kv">
                <span>Price</span>
                {/* What this household was actually charged, not the USD list price: a
                    household that bought in euros is not paying dollars. */}
                <span>{formatMoney(billing.amountMinor, billing.currency)} / year</span>
              </div>
            )}
          </div>
          <div className="row">
            <button className="btn" onClick={() => void openPortal()}>
              Manage billing
            </button>
          </div>
          <p className="hint">
            {billing.store === 'web'
              ? 'Billing is handled by Stripe. Manage billing opens their portal, where you can change your card, see receipts or cancel — your recipes stay put either way.'
              : 'Manage billing opens your store account, where you can change your card or cancel — your recipes stay put either way.'}
          </p>
        </>
      ) : (
        <>
          <p>
            <strong>Free plan.</strong> {quota.used} of {quota.limit ?? 0} recipes used.
          </p>
          <p className="hint">
            {planPriceLoading
              ? 'Checking today’s price…'
              : `${planPrice ?? `$${SUBSCRIPTION_PRICE_USD.toFixed(2)} USD`} a year lifts the cap for everyone in ${
                  active?.name ?? 'this household'
                }.`}
          </p>
          <div className="row">
            <Link className="btn primary" to="/subscribe">
              Subscribe
            </Link>
          </div>
        </>
      )}

      {error != null && <p className="error-text">{error}</p>}
    </section>
  );
}

function storeLabel(store: NonNullable<BillingSummary['store']>): string {
  switch (store) {
    case 'app_store':
      return 'the App Store';
    case 'play_store':
      return 'Google Play';
    case 'complimentary':
      return 'Platewell';
    case 'web':
      return 'the web (billed by Stripe)';
  }
}
